// Bloco reutilizável de envio de mensagens (broadcast) para uma turma da disciplina.
// Renderizado no detalhe da disciplina.
//
// Fluxo: escolhe uma das offerings da disciplina (turma) → escreve título + corpo →
// envia. Backend cria a mensagem + notifica todos os alunos matriculados.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Loader2,
  Send,
  Mail,
  RefreshCw,
  Users
} from "lucide-react";
import { Checkbox } from "@app/ui/components/checkbox";
import { api, ApiError, type SentMessageRow } from "../api";
import { openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface OfferingLite {
  id: string;
  turmaName: string;
  turmaCode?: string | null;
}

const phase = signal<"idle" | "loading" | "ready" | "error">("idle");
const messages = signal<SentMessageRow[]>([]);
const errMsg = signal<string | null>(null);
const loadedFor = signal<string | null>(null);
const sending = signal<boolean>(false);

async function load(offeringId: string) {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listSentMessages(offeringId);
    messages.value = r.messages;
    loadedFor.value = offeringId;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function MensagensSection({
  offerings
}: {
  offerings: OfferingLite[];
}) {
  const [activeOfferingId, setActiveOfferingId] = useState<string>(
    offerings[0]?.id ?? ""
  );
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [notify, setNotify] = useState(true);

  useEffect(() => {
    if (activeOfferingId) {
      void load(activeOfferingId);
    }
  }, [activeOfferingId]);

  async function send() {
    if (!activeOfferingId) return;
    if (!title.trim() || !body.trim()) {
      void openAlert({ title: "Aviso", message: "Preencha título e mensagem." });
      return;
    }
    sending.value = true;
    try {
      const r = await api.sendBroadcast({
        offeringId: activeOfferingId,
        title: title.trim(),
        body: body.trim(),
        notify
      });
      messages.value = [r.message, ...messages.value];
      setTitle("");
      setBody("");
      const turma = offerings.find((o) => o.id === activeOfferingId)?.turmaName;
      void openAlert({
        title: "Mensagem enviada",
        message: `Mensagem enviada para ${r.recipients} aluno(s) de "${turma ?? "turma"}".`
      });
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      sending.value = false;
    }
  }

  if (offerings.length === 0) {
    return (
      <section>
        <h3 class="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <Mail width="1em" height="1em" /> Mensagens
        </h3>
        <p class="text-xs italic text-muted-foreground">
          Sem turmas vinculadas — peça pro admin pra criar uma antes de mandar
          recados.
        </p>
      </section>
    );
  }

  return (
    <section>
      <div class="mb-1.5 flex items-center justify-between">
        <h3 class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <Mail width="1em" height="1em" /> Mensagens
        </h3>
        <button
          type="button"
          onClick={() => activeOfferingId && void load(activeOfferingId)}
          title="Atualizar"
          aria-label="Atualizar"
          class="grid size-7 place-items-center rounded-md border border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <RefreshCw className="size-3" />
        </button>
      </div>

      {/* Composer */}
      <div class="space-y-2 rounded-md border border-border bg-background/50 p-3">
        {offerings.length > 1 && (
          <label class="block">
            <span class="mb-1 block flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <Users width="0.9em" height="0.9em" /> Turma
            </span>
            <select
              value={activeOfferingId}
              onChange={(e) => setActiveOfferingId((e.target as HTMLSelectElement).value)}
              class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
            >
              {offerings.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.turmaName}{o.turmaCode ? ` (${o.turmaCode})` : ""}
                </option>
              ))}
            </select>
          </label>
        )}
        <input
          type="text"
          value={title}
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          placeholder="Título"
          maxLength={200}
          class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        />
        <textarea
          value={body}
          onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
          placeholder="Escreva a mensagem… (até 5000 caracteres)"
          rows={4}
          maxLength={5000}
          class="w-full resize-none rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        />
        <div class="flex items-center justify-between gap-2">
          <label class="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <Checkbox
              checked={notify}
              onCheckedChange={(v) => setNotify(v === true)}
            />
            Notificar alunos
          </label>
          <button
            type="button"
            onClick={send}
            disabled={sending.value || !title.trim() || !body.trim()}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {sending.value ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <Send className="size-3" />
            )}
            Enviar
          </button>
        </div>
      </div>

      {/* Histórico */}
      <div class="mt-2 space-y-1">
        {phase.value === "loading" && (
          <p class="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Loader2 className="size-3 animate-spin" /> Carregando…
          </p>
        )}
        {phase.value === "error" && (
          <p class="flex items-center gap-1.5 text-[11px] text-destructive">
            <AlertCircle className="size-3" /> {errMsg.value}
          </p>
        )}
        {phase.value === "ready" && messages.value.length === 0 && (
          <p class="text-[11px] italic text-muted-foreground">
            Nenhuma mensagem enviada ainda nesta turma.
          </p>
        )}
        {messages.value.map((m) => (
          <div
            key={m.id}
            class="rounded-md border border-border bg-background/40 px-2.5 py-1.5"
          >
            <div class="flex items-center gap-2">
              <span class="truncate text-[11px] font-bold">{m.title}</span>
              <span class="shrink-0 text-[10px] text-muted-foreground">
                {new Date(m.createdAt).toLocaleString("pt-BR")}
              </span>
            </div>
            <p class="mt-0.5 line-clamp-2 text-[10px] text-muted-foreground">
              {m.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
